import { memo, useEffect, useState } from "react";
import Select from "react-select";
import { useGetDepartmentsQuery } from "../../../features/staticDataSlice/staticDataSlice";
import toast from "react-hot-toast";
import { useDebounce } from "../../../hooks/useDebounce";
import "./Selectors.css";

const DepartmentSelect = ({
  label,
  value,
  onChange,
  isDisabled = false,
  placeholder = "Select",
}) => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState([]);
  const debounceSearch = useDebounce(query);

  const {
    data: departmentsData,
    error: departmentsError,
    isFetching,
  } = useGetDepartmentsQuery({
    query: debounceSearch,
    key: "",
    page,
    pageSize,
  });

  useEffect(() => {
    if (departmentsData) {
      const { items } = departmentsData;
      const newOptions = items?.map((department) => ({
        label: `${department.key} - ${department.description}`,
        value: department.id,
      }));

      setOptions((prevOptions) => {
        const seen = new Set();
        const mergedOptions = [
          ...prevOptions,
          ...newOptions,
          ...(value ? [value] : []),
        ].filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });

        return mergedOptions;
      });

      setHasMore(items?.length >= pageSize);
    }

    if (departmentsError) {
      toast.error(departmentsError?.data?.Message || "Failed to load");
    }
  }, [departmentsData, departmentsError, pageSize, value]);

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const selectedOption =
    options.find((opt) =>
      typeof value === "object" && value !== null
        ? opt.value === value.value
        : opt.value === value
    ) || null;

  const handleSearch = (inputValue) => {
    setQuery(inputValue);
    setPage(1);
    if (inputValue) {
      setOptions([]);
    }
  };

  const handleChange = (selected) => {
    onChange(selected || null);
    if (!selected) {
      handleSearch("");
    }
  };

  return (
    <>
      {label && <label className="custom-label">{label}</label>}
      <Select
        className="paginated-selector"
        classNamePrefix="custom-select"
        options={options}
        value={selectedOption}
        isDisabled={isDisabled}
        isClearable={true}
        isLoading={isFetching}
        onChange={handleChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={(inputValue, { action }) => {
          if (action === "input-change") {
            handleSearch(inputValue);
          }
        }}
        placeholder={placeholder}
      />
    </>
  );
};

export default memo(DepartmentSelect);
